import React, { createContext, useContext, useReducer } from 'react';

import styled from '@emotion/styled';

// Type definition
export enum SegmentedViewReducerActionType {
  CLICK_SEGMENT = 'segment-clicked',
}

export interface ISegmentedViewReducerActionType {
  type: SegmentedViewReducerActionType;
  payload: any;
}

interface IsegmentedViewControllerTitle {
  title: string;
}

type State = { index: number };

const SegmentedViewContext = createContext<State>({ index: 0 });

export const useSegmentedViewIndex = () => {
  const { index } = useContext(SegmentedViewContext);
  return index;
};

interface ISegmentedViewData {
  children: React.ReactNode[] | React.ReactNode | any;
  state?: State;
  handleStateChange?: (...args: any) => void;
}

interface ISegmentedViewController extends ISegmentedViewData {
  segmentedViewControllerTitle: IsegmentedViewControllerTitle[];
  className?: string;
}

// Reducer

function segmentedViewReducer(
  state: any,
  action: ISegmentedViewReducerActionType,
) {
  switch (action.type) {
    case SegmentedViewReducerActionType.CLICK_SEGMENT:
      return { ...action.payload };
    default:
      return state;
  }
}

// Components

export const SegmentedView: React.FC<Pick<ISegmentedViewData, 'children'>> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(segmentedViewReducer, { index: 0 });

  const handleStateChange = (index: any) =>
    dispatch({
      type: SegmentedViewReducerActionType.CLICK_SEGMENT,
      payload: { index },
    });

  return (
    <SegmentedViewContext.Provider value={state}>
      <SegmentedViewContainer>
        {React.Children.map(children, (child: any) => {
          if (child.type === SegmentedViewController) {
            return React.cloneElement(child, {
              ...child.props,
              state,
              handleStateChange,
            });
          } else if (child.type === SegmentedViewData) {
            return React.cloneElement(child, { ...child.props, state });
          }
        })}
      </SegmentedViewContainer>
    </SegmentedViewContext.Provider>
  );
};

export const SegmentedViewController: React.FC<
  Omit<ISegmentedViewController, 'children'>
> = ({ segmentedViewControllerTitle, state, handleStateChange, className }) => {
  return (
    <SegmentedViewControllerWrapper className={className}>
      {segmentedViewControllerTitle.map((data, index) => (
        <div
          className={state?.index === index ? 'activeSegment' : ''}
          onClick={() => handleStateChange!(index)}
          key={index}
        >
          {data.title}
        </div>
      ))}
    </SegmentedViewControllerWrapper>
  );
};

export const SegmentedViewData: React.FC<
  Omit<ISegmentedViewData, 'handleStateChange'>
> = ({ children, state }) => {
  return (
    <SegmentData>
      {React.Children.map(children, (child: any, index) => {
        const computedClass = state?.index === index ? 'showSegment' : '';
        return React.cloneElement(child, {
          ...child.props,
          className: computedClass,
        });
      })}
    </SegmentData>
  );
};

// Styles

const SegmentData = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  & > * {
    display: none;
    width: 100%;
  }

  & > .showSegment {
    display: flex;

    @media (max-width: 884px) {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    @media (max-width: 480px) {
      width: 100%;
    }
  }

  & > .showSegment > * {
    /* @media (max-width: 480px) {
      width: 100%;
    } */
  }
`;

const SegmentedViewControllerWrapper = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  gap: 32px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.borderColor};
  width: fit-content;
  border-radius: 6px;
  padding: 4px;
  background-color: ${({ theme }) => theme.palette.lightNeutralGray};

  @media (max-width: 884px) {
    &.mobile-thin {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 12px;
      box-shadow: none;
      border-radius: 0px;
      background-color: ${({ theme }) => theme.palette.bgWhiteColor};
      border-bottom: 1px solid transparent;
    }
  }

  & > * {
    text-align: left;
    line-height: 14px;
    font-size: 14px;
    padding: 11px 27px;
    font-weight: 400;
    color: ${({ theme }) => theme.palette.neutralGray};
    cursor: pointer;
    width: fit-content;
  }

  @media (max-width: 884px) {
    &.mobile-thin > * {
      padding: 11px 0px;
      font-size: 12px;
      font-weight: 400;
      box-shadow: none;
      border-radius: 0px;
      border-bottom: 1px solid transparent;
    }
  }

  & > *:hover {
  }

  & > .activeSegment {
    padding: 10px 26px;
    color: ${({ theme }) => theme.palette.logirateBlack};
    border: 1px solid ${({ theme }) => theme.palette.grayBorder};
    font-weight: 700;
    border-radius: 6px;
    background-color: ${({ theme }) => theme.palette.bgWhiteColor};
    box-shadow: 0px 1px 1px 0px #1f213040;
    box-shadow: 0px 0px 10px 0px #1f21300f;
    box-shadow: 0px 2px 1px 0px #ffffffb2 inset;
  }

  @media (max-width: 884px) {
    &.mobile-thin > .activeSegment {
      box-shadow: none;
      font-weight: 700;
      padding: 11px 0px;
      font-size: 12px;
      border: none;
      border-radius: 0px;
      border-bottom: 1px solid ${({ theme }) => theme.palette.blueColor};
    }
  }
`;

const SegmentedViewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 27px;
  width: 100%;
  height: fit-content;

  @media (max-width: 480px) {
    width: 100%;
    margin-bottom: 3rem;
  }
`;
